import type { Permission, Screen } from './types'

/**
 * The pure logic behind the screen tick boxes shared by Administration → Roles
 * and the person panel (admin/permissionBoxes.tsx).
 *
 * One tick box is one screen; a screen with two permissions grants or withholds
 * both at once — mirrors the server's own rule (internal/org/grants.go's
 * Screens, and roleAsked/mayWriteRole in internal/api/roles.go).
 */

/** Whether every permission a screen needs is in the set held. */
export function screenTicked(screen: Screen, held: readonly Permission[]): boolean {
  return screen.permissions.every((p) => held.includes(p))
}

/** "+" when this screen is ticked now but was not in `baseline`, "−" when it was
 *  and no longer is, and nothing when it agrees with the baseline. Undefined
 *  `baseline` (no comparison asked for) always answers nothing. */
export function screenMark(
  screen: Screen,
  held: readonly Permission[],
  baseline?: readonly Permission[],
): '+' | '−' | undefined {
  if (!baseline) return undefined
  const now = screenTicked(screen, held)
  const before = screenTicked(screen, baseline)
  if (now && !before) return '+'
  if (!now && before) return '−'
  return undefined
}

/** A screen is disabled when the caller may not hand out every permission it
 *  needs — `mayGive` is the server's own answer to that (GET /permissions). */
export function screenDisabled(screen: Screen, mayGive: readonly Permission[]): boolean {
  return !screen.permissions.every((p) => mayGive.includes(p))
}

/** The next held set after one screen's box is ticked or unticked. */
export function toggleScreen(screen: Screen, held: readonly Permission[], ticked: boolean): Permission[] {
  if (ticked) return [...held, ...screen.permissions.filter((p) => !held.includes(p))]
  return held.filter((p) => !screen.permissions.includes(p))
}
